/* eslint-disable consistent-return */
import {
  ADD_BULK_CLIENT_TO_ORG_MUTATION,
  ADD_CLIENT_TO_ORG_MUTATION,
  ALL_PENDING_USERS_BY_ORG_REQUESTS_QUERY,
  CHANGE_ORGANIZATION_DEMO_EMPLOYEES_MUTATION,
  CHANGE_ORGANIZATION_ANALYTICS_EVENTS_MUTATION,
  CHANGE_ORGANIZATION_PREVIEW_MODE_MUTATION,
  CREATE_CLIENT_ORG_MUTATION,
  Client,
  ClientInput,
  ClientOrganization,
  ClientOrganizationInput,
  DELETE_CLIENTS_FROM_ORG_MUTATION,
  DELETE_ORGANIZATION_MUTATION,
  DeleteClientInput,
  DeleteOrganizationInput,
  GET_ORGANIZATION_DEMO_EMPLOYEES_STATUS_QUERY,
  GET_ORGANIZATION_ANALYTICS_EVENTS_STATUS_QUERY,
  GET_ORGANIZATION_PREVIEW_MODE_STATUS_QUERY,
  OrgIdsByFeatureFlagConfig,
  OrganizationClientsFilter,
  OrganizationClientsPage,
  OrganizationFeature,
  OrganizationFeatureInput,
  GET_ORG_IDS_BY_FEATURE_FLAGS_CONFIG,
  INVITE_CLIENT,
  SUPERADMIN_ORGANIZATION_ADMIN_CLIENT_QUERY,
  SUPERADMIN_ORGANIZATION_CLIENTS_QUERY,
  SUPERADMIN_ORGANIZATION_QUERY,
  SUPERADMIN_ORGANIZATIONS_QUERY,
  UPDATE_CLIENT_DETAIL_MUTATION,
  UPDATE_CLIENT_EMAIL_MUTATION,
  UPDATE_CLIENT_ORGANIZATION_MUTATION,
  UPDATE_ORGANIZATION_FEATURE_MUTATION,
  UpdateClientInput,
  UpdateClientOrganizationInput,
} from '@graphql';
import { store } from '@store';
import { graphqlApi } from '@utils';
import { flow, makeAutoObservable } from 'mobx';
import { toast } from 'react-toastify';
import {
  DEFAULT_HTTP_RESPONSE,
  handleError,
  handleMutationResponse,
  handleQueryError,
  handleQueryResponse,
} from './defaults';

/** Page size used when every client of an organization is needed at once (CSV export). */
const ALL_CLIENTS_PAGE_SIZE = 1000;

/** First Admin client of the organization, or null when there is none or the lookup fails. */
async function fetchAdminClient(orgId: string): Promise<Client | null> {
  try {
    const response = await graphqlApi(SUPERADMIN_ORGANIZATION_ADMIN_CLIENT_QUERY, { orgId });
    const page = handleQueryResponse<OrganizationClientsPage>(
      response,
      'superAdminOrganizationClients',
    );
    return page?.items?.[0] ?? null;
  } catch {
    return null;
  }
}

export interface OrganizationClientsQuery {
  offset: number;
  limit: number;
  filter?: OrganizationClientsFilter;
}

export class OrganizationsStore {
  orgIdsByFeatureFlagConfig: OrgIdsByFeatureFlagConfig[] = [];

  /** Organizations list (Organizations page). Holds client counts, never client rows. */
  organizations: ClientOrganization[] = [];

  organizationsResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  private organizationsLoaded = false;

  private organizationsRequestSeq = 0;

  /** Organization open on the detail or billing page; `{}` until loaded. */
  organizationInContext: ClientOrganization = {} as ClientOrganization;

  organizationResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  private organizationRequestSeq = 0;

  /** Current page of the organization's clients (detail page). */
  organizationClients: Client[] = [];

  organizationClientsTotal = 0;

  organizationClientsResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  /** Query the current `organizationClients` page answers (null until a page is loaded). */
  organizationClientsLoadedQuery: OrganizationClientsQuery | null = null;

  private lastClientsRequest: { orgId: string; query: OrganizationClientsQuery } | null = null;

  private clientsRequestSeq = 0;

  createClientOrgResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  addClientToOrgResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  deleteClientFromOrgResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  inviteClientFromOrgResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  addBulkClientToOrgResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  // assignAdvisorToClientResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  deleteOrganizationResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  updateOrganizationResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  updateOrganizationFeatureResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  updateClientDetailResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  updateClientEmailResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  changeOrganizationPreviewModeResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  changeOrganizationDemoEmployeesResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  changeOrganizationAnalyticsEventsResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  constructor() {
    makeAutoObservable(this);
  }

  createClientOrg = flow(function* createClientOrg(
    this: OrganizationsStore,
    variables: ClientOrganizationInput,
  ): any {
    try {
      if (variables.managerId === ' ') {
        toast('Please assign an account manager to the organization.', { type: 'error' });
        return;
      }

      if (variables.advisorId === ' ') {
        toast('Please assign an advisor to the organization.', { type: 'error' });
        return;
      }

      const manager = variables.managerId !== ' ' ? variables.managerId : null;
      const advisor = variables.advisorId !== ' ' ? variables.advisorId : null;

      this.createClientOrgResponse.status = 'pending';
      const userResponse = yield graphqlApi(CREATE_CLIENT_ORG_MUTATION, {
        input: {
          ...variables,
          managerId: manager,
          advisorId: advisor,
        },
      });
      this.createClientOrgResponse.status = 'ok';
      if (userResponse?.data?.errors?.[0]?.message === 'Unauthorized') {
        store.userStore.clearSession();
        return null;
      }
      const userInfo = userResponse?.data?.data?.createClientOrganization;
      if (userInfo) toast('Organization created.', { type: 'success' });
      else toast('Account already exist.', { type: 'info' });
      return userInfo;
    } catch (err) {
      toast('Something went wrong', { type: 'success' });
      this.createClientOrgResponse.status = 'badRequest';
      this.createClientOrgResponse.errMessage = 'Bad Request';
    }
  }).bind(this);

  addClientToOrg = flow(function* addClientToOrg(
    this: OrganizationsStore,
    variables: ClientInput,
  ): any {
    try {
      this.addClientToOrgResponse.status = 'pending';
      const userResponse = yield graphqlApi(ADD_CLIENT_TO_ORG_MUTATION, variables);
      this.addClientToOrgResponse.status = 'ok';
      if (userResponse?.data?.errors?.[0]?.message === 'Unauthorized') {
        store.userStore.clearSession();
        return null;
      }
      // A rejected input (e.g. an invalid name) comes back as a GraphQL error with no data; report it
      // instead of falling through to the "already exists" message.
      const errorMessage = userResponse?.data?.errors?.[0]?.message;
      if (errorMessage) {
        toast(
          errorMessage.includes('duplicate key value violates unique')
            ? 'User with this email already exists'
            : errorMessage,
          { type: 'error' },
        );
        this.addClientToOrgResponse.status = 'badRequest';
        this.addClientToOrgResponse.errMessage = errorMessage;
        return null;
      }
      const userInfo = userResponse?.data?.data?.addClientToOrg;
      if (userInfo) toast('Client Added', { type: 'success' });
      else toast('Account already exist.', { type: 'info' });
      return userInfo;
    } catch (err) {
      toast('Something went wrong', { type: 'error' });
      this.addClientToOrgResponse.status = 'badRequest';
      this.addClientToOrgResponse.errMessage = 'Bad Request';
    }
  }).bind(this);

  deleteClientsFromOrg = flow(function* deleteClientFromOrg(
    this: OrganizationsStore,
    variables: DeleteClientInput,
  ): any {
    try {
      this.deleteClientFromOrgResponse.status = 'pending';
      const response = yield graphqlApi(DELETE_CLIENTS_FROM_ORG_MUTATION, variables);
      this.deleteClientFromOrgResponse.status = 'ok';
      const isClientDeleted = response?.data?.data?.deleteClientsFromOrg;
      if (isClientDeleted) {
        toast('Client deleted from organization', { type: 'success' });
      } else {
        toast('Client not deleted from the organization', { type: 'error' });
      }
      return isClientDeleted;
    } catch (err) {
      toast('Error deleting client from the organization', { type: 'error' });
      this.deleteClientFromOrgResponse.status = 'badRequest';
      this.deleteClientFromOrgResponse.errMessage = 'Bad Request';
    }
  }).bind(this);

  inviteClient = flow(function* inviteClientFromOrg(
    this: OrganizationsStore,
    variables: {
      orgId: string;
      clientIds: string[];
    },
  ): any {
    try {
      this.inviteClientFromOrgResponse.status = 'pending';
      const { orgId, clientIds } = variables;
      const response = yield graphqlApi(INVITE_CLIENT, {
        orgId,
        clientIds,
      });
      this.inviteClientFromOrgResponse.status = 'ok';
      const isClientInvited = response?.data?.data?.sendInviteEmails;
      if (isClientInvited) {
        toast('Client invitation sent', { type: 'success' });
      } else {
        toast('Client invitation failed', { type: 'error' });
      }
      return isClientInvited;
    } catch (err) {
      toast('Error inviting client', { type: 'error' });
      this.inviteClientFromOrgResponse.status = 'badRequest';
      this.inviteClientFromOrgResponse.errMessage = 'Bad Request';
    }
  }).bind(this);

  addBulkClientToOrg = flow(function* addBulkClientToOrg(
    this: OrganizationsStore,
    variables: ClientInput[],
    id: string,
  ): any {
    try {
      this.addBulkClientToOrgResponse.status = 'pending';
      const userResponse = yield graphqlApi(ADD_BULK_CLIENT_TO_ORG_MUTATION, {
        input: variables,
        org_id: id,
      });
      this.addBulkClientToOrgResponse.status = 'ok';
      if (userResponse?.data?.errors?.[0]?.message === 'Unauthorized') {
        store.userStore.clearSession();
        return null;
      }
      if (userResponse?.data?.errors?.length > 0) {
        throw new Error(userResponse?.data?.errors[0]?.message);
      }
      const userInfo = userResponse?.data?.data?.bulkImportClients;
      if (userInfo) toast('User account(s) added', { type: 'success' });
      else toast('Account(s) already exist.', { type: 'info' });
      return userInfo;
    } catch (err) {
      toast(err instanceof Error && err.message ? err.message : 'Something went wrong', { type: 'error' });
      this.addBulkClientToOrgResponse.status = 'badRequest';
      this.addBulkClientToOrgResponse.errMessage = 'Bad Request';
      return null;
    }
  }).bind(this);

  // assignAdvisorToClient = flow(function* assignAdvisorToClient(
  //   this: OrganizationsStore,
  //   variables: AssignAdvisorToClientInput,
  // ): any {
  //   try {
  //     this.assignAdvisorToClientResponse.status = 'pending';
  //     const userResponse = yield graphqlApi(ASSIGN_ADVISOR_TO_CLIENT_MUTATION, variables);
  //     this.assignAdvisorToClientResponse.status = 'ok';
  //     if (userResponse?.data?.errors?.[0]?.message === 'Unauthorized') {
  //       store.userStore.clearSession();
  //       return null;
  //     }
  //     const userInfo = userResponse?.data?.data?.assignAdvisorToClient;
  //     if (userInfo) toast('Advisor assigned to client', { type: 'success' });
  //     else toast('Advisor update failed', { type: 'info' });
  //     return userInfo;
  //   } catch (err) {
  //     toast('Something went wrong', { type: 'error' });
  //     this.assignAdvisorToClientResponse.status = 'badRequest';
  //     this.assignAdvisorToClientResponse.errMessage = 'Bad Request';
  //   }
  // }).bind(this);

  deleteOrganization = flow(function* deleteOrganization(
    this: OrganizationsStore,
    variables: DeleteOrganizationInput,
  ): any {
    try {
      this.deleteOrganizationResponse.status = 'pending';
      const response = yield graphqlApi(DELETE_ORGANIZATION_MUTATION, variables);
      this.deleteOrganizationResponse.status = 'ok';
      const data = handleMutationResponse(response, 'deleteOrganization');
      if (data) {
        toast('Organization deleted', { type: 'success' });
        return data;
      }

      return data;
    } catch (err) {
      toast(`Failed to delete organization: ${handleError(err)}`, { type: 'error' });
      this.deleteOrganizationResponse.status = 'badRequest';
      this.deleteOrganizationResponse.errMessage = 'Bad Request';
    }
  }).bind(this);

  updateOrganizationFeature = flow(function* updateOrganizationFeature(
    this: OrganizationsStore,
    variables: OrganizationFeatureInput,
  ): any {
    try {
      this.updateOrganizationFeatureResponse.status = 'pending';
      const response = yield graphqlApi(UPDATE_ORGANIZATION_FEATURE_MUTATION, {
        organizationFeature: variables,
      });
      this.updateOrganizationFeatureResponse.status = 'ok';
      const data: OrganizationFeature = handleMutationResponse(response, 'updateOrganizationFeature');
      return data;
    } catch (err) {
      toast(`Failed to update feature: ${handleError(err)}`, { type: 'error' });
      this.updateOrganizationFeatureResponse.status = 'badRequest';
      this.updateOrganizationFeatureResponse.errMessage = 'Bad Request';
    }
  }).bind(this);

  updateOrganization = flow(function* updateOrganization(
    this: OrganizationsStore,
    variables: UpdateClientOrganizationInput,
  ): any {
    try {
      this.updateOrganizationResponse.status = 'pending';
      const response = yield graphqlApi(UPDATE_CLIENT_ORGANIZATION_MUTATION, variables);
      this.updateOrganizationResponse.status = 'ok';
      if (response?.data?.errors?.[0]?.message === 'Unauthorized') {
        store.userStore.clearSession();
        return null;
      }
      toast('Organization updated', { type: 'success' });
      return response?.data?.data?.updateClientOrganization;
    } catch (err) {
      toast('Something went wrong', { type: 'error' });
      this.updateOrganizationResponse.status = 'badRequest';
      this.updateOrganizationResponse.errMessage = 'Bad Request';
    }
  }).bind(this);

  updateClientDetail = flow(function* updateClientDetail(
    this: OrganizationsStore,
    variables: UpdateClientInput,
  ): any {
    try {
      this.updateClientDetailResponse.status = 'pending';
      const response = yield graphqlApi(UPDATE_CLIENT_DETAIL_MUTATION, variables);
      this.updateClientDetailResponse.status = 'ok';

      if (response?.data?.errors?.[0]?.message === 'Unauthorized') {
        store.userStore.clearSession();
        return null;
      }

      if (response?.data?.errors?.[0]?.message?.includes('duplicate key value violates unique')) {
        toast('User with this email already exists', { type: 'error' });
        this.updateClientDetailResponse.status = 'badRequest';
        this.updateClientDetailResponse.errMessage = 'Bad Request';

        return null;
      }

      // Any other error means nothing was saved; report it rather than claiming success.
      const errorMessage = response?.data?.errors?.[0]?.message;
      if (errorMessage) {
        toast(errorMessage, { type: 'error' });
        this.updateClientDetailResponse.status = 'badRequest';
        this.updateClientDetailResponse.errMessage = errorMessage;

        return null;
      }

      toast('Client updated', { type: 'success' });

      return response?.data?.data?.updateClientDetail;
    } catch (err) {
      toast('Something went wrong', { type: 'error' });
      this.updateClientDetailResponse.status = 'badRequest';
      this.updateClientDetailResponse.errMessage = 'Bad Request';
    }
  }).bind(this);

  updateClientEmail = flow(function* updateClientDetail(
    this: OrganizationsStore,
    variables: { id: string; email: string },
  ): any {
    try {
      this.updateClientEmailResponse.status = 'pending';
      const response = yield graphqlApi(UPDATE_CLIENT_EMAIL_MUTATION, variables);
      this.updateClientDetailResponse.status = 'ok';
      if (response?.data?.errors?.[0]?.message === 'Unauthorized') {
        store.userStore.clearSession();
        return null;
      }
      if (response?.data.data.updateClientEmail.message === 'client_already_exists') {
        toast('Client already exists', { type: 'error' });
        this.updateClientEmailResponse.status = 'badRequest';
        this.updateClientEmailResponse.errMessage = 'Bad Request';
        return null;
      }
      if (response?.data?.data?.updateClientEmail?.message === 'firebase_error') {
        toast('Something went wrong', { type: 'error' });
        this.updateClientEmailResponse.status = 'badRequest';
        this.updateClientEmailResponse.errMessage = 'Bad Request';
        return null;
      }
      toast('Client updated', { type: 'success' });
      return response?.data?.data?.updateClientEmail;
    } catch (err) {
      toast('Something went wrong', { type: 'error' });
      this.updateClientEmailResponse.status = 'badRequest';
      this.updateClientEmailResponse.errMessage = 'Bad Request';
    }
  }).bind(this);

  getAllPendingUsersByOrg = flow(function* getAllPendingUsersByOrg(
    this: OrganizationsStore,
    variables: { orgId: string },
  ): any {
    try {
      const response = yield graphqlApi(ALL_PENDING_USERS_BY_ORG_REQUESTS_QUERY, variables);
      return response?.data?.data?.allPendingUsersByOrg;
    } catch (err) {
      toast('Something went wrong', { type: 'error' });
    }
  }).bind(this);

  /**
   * Loads the organizations list. A response that arrives after the list page unmounted
   * (resetOrganizations) or after a newer request is dropped.
   */
  fetchOrganizations = flow(function* fetchOrganizations(this: OrganizationsStore): any {
    this.organizationsRequestSeq += 1;
    const requestSeq = this.organizationsRequestSeq;

    try {
      this.organizationsResponse.status = 'pending';
      const response = yield graphqlApi(SUPERADMIN_ORGANIZATIONS_QUERY);
      const organizations = handleQueryResponse<ClientOrganization[]>(
        response,
        'superAdminOrganizations',
      );
      if (requestSeq !== this.organizationsRequestSeq) return;

      this.organizations = organizations ?? [];
      this.organizationsLoaded = true;
      this.organizationsResponse.status = 'ok';
      return this.organizations;
    } catch (err) {
      if (requestSeq !== this.organizationsRequestSeq) return;
      handleQueryError(err, this.organizationsResponse);
    }
  }).bind(this);

  /**
   * Loads the organization in context. The admin email is a separate request, so a failure there
   * never hides the organization. Stale responses (newer request, page unmounted) are dropped.
   */
  fetchOrganization = flow(function* fetchOrganization(this: OrganizationsStore, id: string): any {
    this.organizationRequestSeq += 1;
    const requestSeq = this.organizationRequestSeq;

    if (this.organizationInContext?.id !== id) {
      this.organizationInContext = {} as ClientOrganization;
    }

    try {
      this.organizationResponse.status = 'pending';
      const [response, adminClient] = yield Promise.all([
        graphqlApi(SUPERADMIN_ORGANIZATION_QUERY, { id }),
        fetchAdminClient(id),
      ]);
      const organization = handleQueryResponse<ClientOrganization>(
        response,
        'superAdminOrganization',
      );
      if (requestSeq !== this.organizationRequestSeq) return;

      this.organizationInContext = organization
        ? { ...organization, adminClient }
        : ({} as ClientOrganization);
      this.organizationResponse.status = 'ok';
      return organization;
    } catch (err) {
      if (requestSeq !== this.organizationRequestSeq) return;
      handleQueryError(err, this.organizationResponse);
    }
  }).bind(this);

  /**
   * Loads one page of an organization's clients. Responses that arrive after a newer request was
   * made (e.g. while the search is being typed) are dropped.
   */
  fetchOrganizationClients = flow(function* fetchOrganizationClients(
    this: OrganizationsStore,
    orgId: string,
    query: OrganizationClientsQuery,
  ): any {
    this.clientsRequestSeq += 1;
    const requestSeq = this.clientsRequestSeq;
    this.lastClientsRequest = { orgId, query };

    try {
      this.organizationClientsResponse.status = 'pending';
      const response = yield graphqlApi(SUPERADMIN_ORGANIZATION_CLIENTS_QUERY, { orgId, ...query });
      const page = handleQueryResponse<OrganizationClientsPage>(
        response,
        'superAdminOrganizationClients',
      );
      if (requestSeq !== this.clientsRequestSeq) return;

      this.organizationClients = page?.items ?? [];
      this.organizationClientsTotal = page?.totalCount ?? 0;
      this.organizationClientsLoadedQuery = query;
      this.organizationClientsResponse.status = 'ok';
      return page;
    } catch (err) {
      if (requestSeq !== this.clientsRequestSeq) return;
      handleQueryError(err, this.organizationClientsResponse);
    }
  }).bind(this);

  /** Reloads the client page last requested for this organization (after a client mutation). */
  refreshOrganizationClients = flow(function* refreshOrganizationClients(
    this: OrganizationsStore,
    orgId: string,
  ): any {
    if (this.lastClientsRequest?.orgId !== orgId) return;
    yield this.fetchOrganizationClients(orgId, this.lastClientsRequest.query);
  }).bind(this);

  /** Reloads the organization and its current client page after clients were added or changed. */
  refreshOrganizationDetail = flow(function* refreshOrganizationDetail(
    this: OrganizationsStore,
    orgId: string,
  ): any {
    yield Promise.all([this.fetchOrganization(orgId), this.refreshOrganizationClients(orgId)]);
    return true;
  }).bind(this);

  /**
   * After an organization changed, reloads only the views that are showing it: the organizations
   * list (while the Organizations page is open) and the organization in context (detail/billing).
   */
  refreshOrganizationViews = flow(function* refreshOrganizationViews(
    this: OrganizationsStore,
    orgId?: string,
  ): any {
    const requests: Promise<unknown>[] = [];
    if (this.organizationsLoaded) requests.push(this.fetchOrganizations());
    if (orgId && this.organizationInContext?.id === orgId) {
      requests.push(this.fetchOrganization(orgId));
    }
    yield Promise.all(requests);
    return true;
  }).bind(this);

  /** Called when the Organizations page unmounts. */
  resetOrganizations = () => {
    this.organizationsRequestSeq += 1;
    this.organizations = [];
    this.organizationsLoaded = false;
  };

  /** Called when the organization detail or billing page unmounts. */
  resetOrganizationInContext = () => {
    // Responses still in flight for the page being left are dropped.
    this.organizationRequestSeq += 1;
    this.clientsRequestSeq += 1;
    this.organizationInContext = {} as ClientOrganization;
    this.organizationClients = [];
    this.organizationClientsTotal = 0;
    this.organizationClientsLoadedQuery = null;
    this.lastClientsRequest = null;
  };

  /** Every client of the organization, page by page. Not kept in the store. */
  fetchAllOrganizationClients = flow(function* fetchAllOrganizationClients(
    this: OrganizationsStore,
    orgId: string,
  ): any {
    try {
      const clients: Client[] = [];
      let totalCount = Infinity;
      while (clients.length < totalCount) {
        const response = yield graphqlApi(SUPERADMIN_ORGANIZATION_CLIENTS_QUERY, {
          orgId,
          offset: clients.length,
          limit: ALL_CLIENTS_PAGE_SIZE,
        });
        const page = handleQueryResponse<OrganizationClientsPage>(
          response,
          'superAdminOrganizationClients',
        );
        clients.push(...(page?.items ?? []));
        totalCount = page?.totalCount ?? 0;
        if (!page?.items?.length) break;
      }
      return clients;
    } catch (err) {
      handleQueryError(err);
      return null;
    }
  }).bind(this);

  getOrgIdsByFeatureFlagConfig = flow(function* getOrgIdsByFeatureFlagConfig(
    this: OrganizationsStore,
  ): any {
    try {
      const response = yield graphqlApi(GET_ORG_IDS_BY_FEATURE_FLAGS_CONFIG);
      this.orgIdsByFeatureFlagConfig = response?.data?.data?.getOrgIdsByFeatureFlagConfig;
      return response?.data?.data?.getOrgIdsByFeatureFlagConfig;
    } catch (err) {
      toast('Something went wrong', { type: 'error' });
    }
  }).bind(this);

  changeOrganizationPreviewMode = flow(function* changeOrganizationPreviewMode(
    this: OrganizationsStore,
    variables: { organizationId: string; isEnabled: boolean },
  ): any {
    try {
      this.changeOrganizationPreviewModeResponse.status = 'pending';
      const response = yield graphqlApi(CHANGE_ORGANIZATION_PREVIEW_MODE_MUTATION, variables);
      this.changeOrganizationPreviewModeResponse.status = 'ok';
      return handleMutationResponse(response, 'changeOrganizationPreviewMode');
    } catch (err) {
      toast(`Failed to update Preview Mode: ${handleError(err)}`, { type: 'error' });
      this.changeOrganizationPreviewModeResponse.status = 'badRequest';
      this.changeOrganizationPreviewModeResponse.errMessage = 'Bad Request';
    }
  }).bind(this);

  getOrganizationPreviewModeStatus = flow(function* getOrganizationPreviewModeStatus(
    this: OrganizationsStore,
    variables: { organizationId: string },
  ): any {
    try {
      const response = yield graphqlApi(GET_ORGANIZATION_PREVIEW_MODE_STATUS_QUERY, variables);
      return response?.data?.data?.getOrganizationPreviewModeStatus;
    } catch (err) {
      toast('Something went wrong', { type: 'error' });
    }
  }).bind(this);

  changeOrganizationDemoEmployees = flow(function* changeOrganizationDemoEmployees(
    this: OrganizationsStore,
    variables: { organizationId: string; isEnabled: boolean },
  ): any {
    try {
      this.changeOrganizationDemoEmployeesResponse.status = 'pending';
      const response = yield graphqlApi(CHANGE_ORGANIZATION_DEMO_EMPLOYEES_MUTATION, variables);
      this.changeOrganizationDemoEmployeesResponse.status = 'ok';
      return handleMutationResponse(response, 'changeOrganizationDemoEmployees');
    } catch (err) {
      toast(`Failed to update Test employees: ${handleError(err)}`, { type: 'error' });
      this.changeOrganizationDemoEmployeesResponse.status = 'badRequest';
      this.changeOrganizationDemoEmployeesResponse.errMessage = 'Bad Request';
    }
  }).bind(this);

  getOrganizationDemoEmployeesStatus = flow(function* getOrganizationDemoEmployeesStatus(
    this: OrganizationsStore,
    variables: { organizationId: string },
  ): any {
    try {
      const response = yield graphqlApi(GET_ORGANIZATION_DEMO_EMPLOYEES_STATUS_QUERY, variables);
      return response?.data?.data?.getOrganizationDemoEmployeesStatus;
    } catch (err) {
      toast('Something went wrong', { type: 'error' });
    }
  }).bind(this);

  changeOrganizationAnalyticsEvents = flow(function* changeOrganizationAnalyticsEvents(
    this: OrganizationsStore,
    variables: { organizationId: string; isEnabled: boolean },
  ): any {
    try {
      this.changeOrganizationAnalyticsEventsResponse.status = 'pending';
      const response = yield graphqlApi(CHANGE_ORGANIZATION_ANALYTICS_EVENTS_MUTATION, variables);
      this.changeOrganizationAnalyticsEventsResponse.status = 'ok';
      return handleMutationResponse(response, 'changeOrganizationAnalyticsEvents');
    } catch (err) {
      toast(`Failed to update analytics events: ${handleError(err)}`, { type: 'error' });
      this.changeOrganizationAnalyticsEventsResponse.status = 'badRequest';
      this.changeOrganizationAnalyticsEventsResponse.errMessage = 'Bad Request';
    }
  }).bind(this);

  getOrganizationAnalyticsEventsStatus = flow(function* getOrganizationAnalyticsEventsStatus(
    this: OrganizationsStore,
    variables: { organizationId: string },
  ): any {
    try {
      const response = yield graphqlApi(GET_ORGANIZATION_ANALYTICS_EVENTS_STATUS_QUERY, variables);
      const status = response?.data?.data?.getOrganizationAnalyticsEventsStatus;
      // GraphQL errors arrive as a 200 with a null field, so anything but a boolean is
      // "unknown" — callers must not read it as off, since prod defaults to on.
      return typeof status === 'boolean' ? status : null;
    } catch (err) {
      toast('Something went wrong', { type: 'error' });
      return null;
    }
  }).bind(this);
}
